import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { ArrowLeft, Package, PenLine, ArrowDownToLine, ArrowUpFromLine, Syringe, FileText, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import type { InventoryItem, InventoryLot, InventoryTransactionRow, Vendor } from '../../types/inventory';
import { getInventoryItems, getInventoryLots, getInventoryTransactions, adjustInventoryDelta, updateInventoryLot, deleteInventoryLot, updateInventoryConsumption, deleteInventoryConsumption } from '../../utils/inventoryApi';
import { listVendors } from '../../utils/vendorsApi';
import { ReceiveStockModal } from './components/ReceiveStockModal';
import { AdjustStockModal } from './components/AdjustStockModal';

type RouteParams = {
  catalogType?: string;
  catalogItemId?: string;
};

// Unified ledger entry combining lots (receipts) and transactions
type LedgerEntry = {
  id: string;
  lotId: string | null; // The actual lot ID for updates (null for transactions without lot)
  date: string;
  type: 'receipt' | 'adjustment_in' | 'adjustment_out' | 'dispensed' | 'unknown';
  typeLabel: string;
  lotNumber: string | null;
  quantity: number;
  unitCost: string | null;
  expirationDate: string | null;
  notes: string | null;
  source: 'lot' | 'transaction';
  isEditable: boolean;
};

// Editable fields
type EditableField = 'type' | 'lotNumber' | 'quantity' | 'unitCost' | 'notes' | 'expirationDate';

// Type options for dropdown
const TYPE_OPTIONS: { value: LedgerEntry['type']; label: string }[] = [
  { value: 'receipt', label: 'Receipt' },
  { value: 'adjustment_in', label: 'Adjustment (+)' },
  { value: 'adjustment_out', label: 'Adjustment (−)' },
  { value: 'dispensed', label: 'Dispensed' },
];

const fmtMoney = (val?: string | null) => {
  if (!val) return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return val;
  return `$${n.toFixed(2)}`;
};

const fmtDate = (val?: string | null) => {
  if (!val) return '—';
  try {
    const d = new Date(val);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return val;
  }
};

const fmtDateTime = (val?: string | null) => {
  if (!val) return '—';
  try {
    const d = new Date(val);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
  return val;
  }
};

function getTypeLabel(sourceType: string): { type: LedgerEntry['type']; label: string } {
  switch (sourceType) {
    case 'po_receive':
      return { type: 'receipt', label: 'Receipt (PO)' };
    case 'manual_adjust_in':
      return { type: 'adjustment_in', label: 'Adjustment (+)' };
    case 'manual_adjust_out':
      return { type: 'adjustment_out', label: 'Adjustment (−)' };
    case 'immunization_admin':
      return { type: 'dispensed', label: 'Dispensed' };
    default:
      return { type: 'unknown', label: sourceType };
  }
}

function TypeIcon({ type }: { type: LedgerEntry['type'] }) {
  switch (type) {
    case 'receipt':
      return <Package className="w-4 h-4 text-emerald-600" />;
    case 'adjustment_in':
      return <ArrowDownToLine className="w-4 h-4 text-emerald-600" />;
    case 'adjustment_out':
      return <ArrowUpFromLine className="w-4 h-4 text-red-600" />;
    case 'dispensed':
      return <Syringe className="w-4 h-4 text-blue-600" />;
    default:
      return <FileText className="w-4 h-4 text-gray-400" />;
  }
}

export const InventoryStatusItemShow: React.FC = () => {
  const { user } = useAuth();
  const practiceId = user?.practice_id;
  const navigate = useNavigate();
  const { catalogType, catalogItemId } = useParams<RouteParams>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [item, setItem] = useState<InventoryItem | null>(null);
  const [lots, setLots] = useState<InventoryLot[]>([]);
  const [txns, setTxns] = useState<InventoryTransactionRow[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Modal states
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);

  // Inline editing state
  const [editingCell, setEditingCell] = useState<{ id: string; field: EditableField } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);

  useEffect(() => {
    const pid = practiceId;
    const ct = (catalogType || '').toLowerCase();
    const cid = catalogItemId;
    if (!pid || !ct || !cid) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [items, lotsData, txnsData, vends] = await Promise.all([
          getInventoryItems(pid),
          getInventoryLots(pid, { catalog_type: ct, catalog_item_id: cid, include_consumed: true }),
          getInventoryTransactions(pid, { catalog_type: ct as any, catalog_item_id: cid }),
          listVendors(pid),
        ]);
        if (cancelled) return;
        setItem(items.find((x) => x.catalog_type === (ct as any) && x.catalog_item_id === cid) || null);
        setLots(lotsData);
        setTxns(txnsData);
        setVendors(vends);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load inventory item');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [practiceId, catalogType, catalogItemId, refreshKey]);

  // Focus input when editing starts
  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      if ('select' in inputRef.current && typeof inputRef.current.select === 'function') {
        inputRef.current.select();
      }
    }
  }, [editingCell]);

  // Reserved is still from lots (that's a lot-level property)
  const reserved = useMemo(() => {
    const activeLots = lots.filter((l) => !l.is_consumed);
    return activeLots.reduce((sum, l) => sum + Number(l.quantity_reserved || 0), 0);
  }, [lots]);

  // avgCost will be calculated from ledger entries after they're created

  // Build unified ledger
  const ledgerEntries = useMemo(() => {
    const entries: LedgerEntry[] = [];
    const seenLotIds = new Set<string>();

    for (const txn of txns) {
      const { type, label } = getTypeLabel(txn.source_type);
      
      const lotNumbers = txn.allocations
        .map((a) => a.lot_number)
        .filter(Boolean)
        .join(', ');
      
      const avgUnitCost = txn.allocations.length > 0
        ? (txn.allocations.reduce((sum, a) => sum + Number(a.unit_cost || 0) * Math.abs(Number(a.quantity || 0)), 0) /
           txn.allocations.reduce((sum, a) => sum + Math.abs(Number(a.quantity || 0)), 0)).toFixed(2)
        : null;

      // Get first lot ID for updates
      const firstLotId = txn.allocations[0]?.lot_id || null;
      for (const a of txn.allocations) {
        if (a.lot_id) seenLotIds.add(a.lot_id);
      }

      // Get expiration date from the linked lot (if any)
      const linkedLot = firstLotId ? lots.find((l) => l.id === firstLotId) : null;
      const lotExpirationDate = linkedLot?.expiration_date ?? null;

      // Determine quantity sign based on source_type
      // Outflow types (consumption) should be negative for balance calculation
      const outflowSourceTypes = ['manual_adjust_out', 'immunization_admin', 'dispensed'];
      const isOutflow = outflowSourceTypes.includes(txn.source_type);
      const displayQuantity = isOutflow ? -Math.abs(txn.quantity) : Math.abs(txn.quantity);

      entries.push({
        id: txn.id,
        lotId: firstLotId,
        date: txn.occurred_at,
        type,
        typeLabel: label,
        lotNumber: lotNumbers || null,
        quantity: displayQuantity,
        unitCost: avgUnitCost,
        expirationDate: lotExpirationDate,
        notes: txn.notes ?? null,
        source: 'transaction',
        isEditable: true, // Transactions are always editable via consumption endpoint
      });
    }

    for (const lot of lots) {
      if (seenLotIds.has(lot.id)) continue;
      
      if (Number(lot.quantity_received || 0) > 0) {
        entries.push({
          id: `lot-${lot.id}`,
          lotId: lot.id,
          date: lot.received_date || lot.created_at,
          type: 'receipt',
          typeLabel: lot.lot_number.startsWith('ADJUST') ? 'Adjustment' : 'Receipt',
          lotNumber: lot.lot_number,
          quantity: Number(lot.quantity_received),
          unitCost: lot.unit_cost_received ?? null,
          expirationDate: lot.expiration_date ?? null,
          notes: lot.notes ?? null,
          source: 'lot',
          isEditable: true,
        });
      }
    }

    entries.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    return entries;
  }, [txns, lots]);

  const ledgerWithBalance = useMemo(() => {
    const chronological = [...ledgerEntries].reverse();
    let balance = 0;
    const withBalance = chronological.map((entry) => {
      balance += entry.quantity;
      return { ...entry, balance };
    });
    return withBalance.reverse();
  }, [ledgerEntries]);

  // Calculate totals from the ledger (source of truth for movements)
  const totals = useMemo(() => {
    // onHand is the sum of all movements (final balance)
    const onHand = ledgerEntries.reduce((sum, e) => sum + e.quantity, 0);
    const available = onHand - reserved;
    return { onHand, reserved, available };
  }, [ledgerEntries, reserved]);

  // Calculate avg cost from ledger entries (only inflows with cost > 0)
  const avgCost = useMemo(() => {
    // Only count inflow entries (positive quantity) with a unit cost
    const inflowsWithCost = ledgerEntries.filter(
      (e) => e.quantity > 0 && Number(e.unitCost || 0) > 0
    );
    if (inflowsWithCost.length === 0) return null;
    
    const totalValue = inflowsWithCost.reduce(
      (sum, e) => sum + Number(e.unitCost || 0) * e.quantity,
      0
    );
    const totalQty = inflowsWithCost.reduce((sum, e) => sum + e.quantity, 0);
    return totalQty > 0 ? (totalValue / totalQty).toFixed(2) : null;
  }, [ledgerEntries]);

  const title = item?.name || `${catalogType || 'Item'} ${catalogItemId || ''}`;

  // Start editing a cell
  const startEdit = useCallback((id: string, field: EditableField, currentValue: string | number | null) => {
    const entry = ledgerEntries.find((e) => e.id === id);
    if (!entry?.isEditable) return;
    
    setEditingCell({ id, field });
    setEditValue(currentValue?.toString() || '');
  }, [ledgerEntries]);

  // Cancel editing
  const cancelEdit = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
  }, []);

  // Save edited value with optimistic update
  const saveEdit = useCallback(async () => {
    if (!editingCell || !practiceId) return;
    
    const entry = ledgerEntries.find((e) => e.id === editingCell.id);
    
    // For type, notes, or quantity changes on transaction entries, update the consumption
    if ((editingCell.field === 'type' || editingCell.field === 'notes' || editingCell.field === 'quantity') && entry?.source === 'transaction') {
      const consumptionId = entry?.id;
      if (!consumptionId) {
        cancelEdit();
        return;
      }

      let consumptionPayload: { source_type?: string; notes?: string; quantity?: number } = {};

      if (editingCell.field === 'type') {
        // Map type option value to source_type
        const sourceTypeMap: Record<string, string> = {
          'adjustment_in': 'manual_adjust_in',
          'adjustment_out': 'manual_adjust_out',
          'receipt': 'po_receive',
          'dispensed': 'immunization_admin',
        };
        const newSourceType = sourceTypeMap[editValue] || editValue;
        consumptionPayload.source_type = newSourceType;
        
        // Determine if switching between inflow and outflow
        const outflowTypes = ['manual_adjust_out', 'immunization_admin', 'dispensed'];
        const oldSourceType = txns.find(t => t.id === consumptionId)?.source_type || '';
        const wasOutflow = outflowTypes.includes(oldSourceType);
        const isNowOutflow = outflowTypes.includes(newSourceType);
        const txnQty = txns.find(t => t.id === consumptionId)?.quantity || 0;
        
        // Optimistically update transactions state for type
        setTxns((prevTxns) =>
          prevTxns.map((txn) => {
            if (txn.id !== consumptionId) return txn;
            // If switching to outflow, clear unit costs from allocations
            const updatedAllocations = isNowOutflow 
              ? txn.allocations.map(a => ({ ...a, unit_cost: '0.00' }))
              : txn.allocations;
            return { ...txn, source_type: newSourceType, allocations: updatedAllocations };
          })
        );
        
        // Update lot quantities if switching between inflow/outflow
        // This affects the current stock calculation
        if (wasOutflow !== isNowOutflow && entry?.lotId) {
          const lotId = entry.lotId;
          setLots((prevLots) =>
            prevLots.map((lot) => {
              if (lot.id !== lotId) return lot;
              // If was outflow and now inflow: add 2x quantity (reverse the subtraction, add)
              // If was inflow and now outflow: subtract 2x quantity
              const qtyChange = wasOutflow ? (2 * txnQty) : (-2 * txnQty);
              return {
                ...lot,
                quantity_on_hand: Math.max(0, lot.quantity_on_hand + qtyChange),
              };
            })
          );
        }
      } else if (editingCell.field === 'notes') {
        // Notes update
        consumptionPayload.notes = editValue.trim();
        
        // Optimistically update transactions state for notes
        setTxns((prevTxns) =>
          prevTxns.map((txn) => {
            if (txn.id !== consumptionId) return txn;
            return { ...txn, notes: consumptionPayload.notes || null };
          })
        );
      } else if (editingCell.field === 'quantity') {
        // Quantity update - parse the value (which may be negative like "-1" or positive like "+12")
        const qty = parseInt(editValue.replace(/[+]/g, ''), 10);
        if (isNaN(qty)) {
          cancelEdit();
          return;
        }
        // Store absolute value - source_type determines direction
        const newAbsQty = Math.abs(qty);
        consumptionPayload.quantity = newAbsQty;
        
        // Find the transaction to get old quantity and source_type
        const txn = txns.find((t) => t.id === consumptionId);
        const oldQty = txn?.quantity || 0;
        const sourceType = txn?.source_type || '';
        const delta = newAbsQty - oldQty;
        
        // Optimistically update transactions state for quantity
        setTxns((prevTxns) =>
          prevTxns.map((t) => {
            if (t.id !== consumptionId) return t;
            return { ...t, quantity: newAbsQty };
          })
        );
        
        // Also update lot quantity_on_hand based on direction
        // For outflows: more consumed = less on hand, so subtract delta
        // For inflows: more received = more on hand, so add delta
        const isOutflow = ['manual_adjust_out', 'immunization_admin', 'dispensed'].includes(sourceType);
        const lotId = entry?.lotId;
        
        if (lotId && delta !== 0) {
          setLots((prevLots) =>
            prevLots.map((lot) => {
              if (lot.id !== lotId) return lot;
              const qtyChange = isOutflow ? -delta : delta;
      return {
                ...lot,
                quantity_on_hand: Math.max(0, lot.quantity_on_hand + qtyChange),
              };
            })
          );
        }
      }

      cancelEdit();
      setSaving(true);
      try {
        await updateInventoryConsumption(practiceId, consumptionId, consumptionPayload);
        // Success - optimistic update was correct, no refresh needed
      } catch (e: any) {
        console.error('Failed to update:', e);
        // On error, refresh to get the real state
        setRefreshKey((x) => x + 1);
      } finally {
        setSaving(false);
      }
      return;
    }

    // For other fields, update the lot
    if (!entry?.lotId) {
      cancelEdit();
      return;
    }

    let payload: Record<string, unknown> = {};
    let isValid = true;
    let optimisticValue: string | number | null = null;
    
    switch (editingCell.field) {
      case 'lotNumber':
        const trimmedLot = editValue.trim();
        if (trimmedLot) {
          payload.lot_number = trimmedLot;
          optimisticValue = trimmedLot;
        } else {
          isValid = false;
        }
        break;
      case 'notes':
        payload.notes = editValue.trim() || '';
        optimisticValue = editValue.trim() || null;
        break;
      case 'quantity':
        const qty = parseInt(editValue, 10);
        if (!isNaN(qty) && qty >= 0) {
          payload.quantity_received = qty;
          optimisticValue = qty;
        } else {
          isValid = false;
        }
        break;
      case 'unitCost':
        const cost = parseFloat(editValue);
        if (!isNaN(cost) && cost >= 0) {
          payload.unit_cost_received = cost.toFixed(2);
          optimisticValue = cost.toFixed(2);
        } else {
          isValid = false;
        }
        break;
      case 'expirationDate':
        // Allow empty value to clear expiration date
        if (editValue.trim()) {
          payload.expiration_date = editValue.trim();
          optimisticValue = editValue.trim();
        } else {
          payload.expiration_date = null;
          optimisticValue = null;
        }
        break;
    }

    // Only proceed if we have valid data
    if (!isValid || Object.keys(payload).length === 0) {
      cancelEdit();
      return;
    }

    // Optimistically update local state BEFORE API call
    const lotIdToUpdate = entry.lotId;
    const fieldToUpdate = editingCell.field;
    
    setLots((prevLots) => 
      prevLots.map((lot) => {
        if (lot.id !== lotIdToUpdate) return lot;
        const updated = { ...lot };
        if (fieldToUpdate === 'lotNumber' && typeof optimisticValue === 'string') {
          updated.lot_number = optimisticValue;
        } else if (fieldToUpdate === 'notes') {
          updated.notes = optimisticValue as string | null;
        } else if (fieldToUpdate === 'quantity' && typeof optimisticValue === 'number') {
          // Update quantity_received and adjust quantity_on_hand by the delta
          const delta = optimisticValue - lot.quantity_received;
          updated.quantity_received = optimisticValue;
          updated.quantity_on_hand = Math.max(0, lot.quantity_on_hand + delta);
        } else if (fieldToUpdate === 'unitCost' && typeof optimisticValue === 'string') {
          updated.unit_cost_received = optimisticValue;
        } else if (fieldToUpdate === 'expirationDate') {
          updated.expiration_date = optimisticValue as string | null;
        }
        return updated;
      })
    );

    // Also update transactions state to reflect the change in allocations
    if (fieldToUpdate === 'unitCost') {
      setTxns((prevTxns) =>
        prevTxns.map((txn) => ({
          ...txn,
          allocations: txn.allocations.map((alloc) => {
            if (alloc.lot_id === lotIdToUpdate) {
              return { ...alloc, unit_cost: optimisticValue as string };
            }
            return alloc;
          }),
        }))
      );
    }

    // Clear editing state immediately for responsive feel
    const savedLotId = lotIdToUpdate;
    cancelEdit();

    setSaving(true);
    try {
      await updateInventoryLot(practiceId, savedLotId, payload);
      // Success - optimistic update was correct, no need to refresh
    } catch (e: any) {
      console.error('Failed to save:', e);
      // On error, refresh to get the real state
      setRefreshKey((x) => x + 1);
    } finally {
      setSaving(false);
    }
  }, [editingCell, editValue, practiceId, ledgerEntries, cancelEdit]);

  // Handle keydown in edit mode
  const handleEditKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  }, [saveEdit, cancelEdit]);

  const handleReceiveStock = async (args: {
    quantity: number;
    unit_cost: string;
    lot_number?: string | null;
    expiration_date?: string | null;
    vendor_id?: string | null;
    notes?: string | null;
  }) => {
    if (!practiceId || !catalogType || !catalogItemId) return;
    
    await adjustInventoryDelta(practiceId, catalogType, catalogItemId, totals.onHand, {
      delta: args.quantity,
      unit_cost_received: args.unit_cost,
      notes: args.notes ? `Received: ${args.notes}` : `Received ${args.quantity} units`,
    });
    
    setRefreshKey((x) => x + 1);
  };

  const handleAdjustStock = async (args: {
    delta: number;
    reason: string;
    notes?: string | null;
  }) => {
    if (!practiceId || !catalogType || !catalogItemId) return;
    
    await adjustInventoryDelta(practiceId, catalogType, catalogItemId, totals.onHand, {
      delta: args.delta,
      unit_cost_received: null,
      notes: args.notes,
    });
    
    setRefreshKey((x) => x + 1);
  };

  // Delete a lot or consumption
  const handleDeleteRow = useCallback(async (entry: typeof ledgerWithBalance[0]) => {
    if (!practiceId) return;
    
    // Confirm deletion
    if (!window.confirm(`Delete this ${entry.typeLabel} entry? This cannot be undone.`)) {
      return;
    }

    // For transaction entries without a lotId, delete the consumption
    if (entry.source === 'transaction' && !entry.lotId) {
      // Optimistically remove from local state
      setTxns((prevTxns) => prevTxns.filter((txn) => txn.id !== entry.id));

      try {
        await deleteInventoryConsumption(practiceId, entry.id);
        // Refresh to get updated data
        setRefreshKey((x) => x + 1);
      } catch (e: any) {
        console.error('Failed to delete:', e);
        setRefreshKey((x) => x + 1);
      }
      return;
    }

    // For entries with a lotId, delete the lot
    if (!entry.lotId) return;

    // Optimistically remove from local state
    setLots((prevLots) => prevLots.filter((lot) => lot.id !== entry.lotId));
    setTxns((prevTxns) => prevTxns.filter((txn) => 
      !txn.allocations.some((a) => a.lot_id === entry.lotId)
    ));

    try {
      await deleteInventoryLot(practiceId, entry.lotId);
    } catch (e: any) {
      console.error('Failed to delete:', e);
      // On error, refresh to restore state
      setRefreshKey((x) => x + 1);
    }
  }, [practiceId]);

  // Render editable cell
  const renderEditableCell = (
    entry: typeof ledgerWithBalance[0],
    field: EditableField,
    displayValue: React.ReactNode,
    currentValue: string | number | null,
    className?: string
  ) => {
    const isEditing = editingCell?.id === entry.id && editingCell?.field === field;
    
    if (!entry.isEditable) {
      return <span className={className}>{displayValue}</span>;
    }

    if (isEditing) {
      if (field === 'type') {
        return (
          <select
            ref={inputRef as React.RefObject<HTMLSelectElement>}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={saveEdit}
            onKeyDown={handleEditKeyDown}
            disabled={saving}
            className="w-full px-2 py-1 text-sm border border-blue-500 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      }

      if (field === 'notes') {
        return (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={saveEdit}
            onKeyDown={handleEditKeyDown}
            disabled={saving}
            rows={2}
            className="w-full px-2 py-1 text-sm border border-blue-500 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        );
      }

      if (field === 'unitCost') {
        return (
          <div className="relative inline-flex">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-sm">$</span>
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type="number"
              step="0.01"
              min="0"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={saveEdit}
              onKeyDown={handleEditKeyDown}
              disabled={saving}
              className="w-24 pl-5 pr-2 py-1 text-sm text-right border border-blue-500 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        );
      }

      if (field === 'expirationDate') {
        return (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="date"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={saveEdit}
            onKeyDown={handleEditKeyDown}
            disabled={saving}
            className="w-36 px-2 py-1 text-sm border border-blue-500 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );
      }

      return (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type={field === 'quantity' ? 'number' : 'text'}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={saveEdit}
          onKeyDown={handleEditKeyDown}
          disabled={saving}
          className={`w-full px-2 py-1 text-sm border border-blue-500 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            field === 'quantity' ? 'text-right' : ''
          }`}
        />
      );
    }

    return (
      <span
        onClick={() => startEdit(entry.id, field, currentValue)}
        className={`cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 px-1 py-0.5 -mx-1 rounded transition-colors ${className || ''}`}
        title="Click to edit"
      >
        {displayValue}
      </span>
    );
  };

  if (!practiceId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-sm text-gray-600 dark:text-gray-300">Select a practice to view inventory.</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-sm text-gray-600 dark:text-gray-300">Loading…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/settings/inventory')}
            className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Inventory
          </button>
          <div className="mt-4 text-sm text-red-600">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button
              onClick={() => navigate('/settings/inventory?tab=inventory-status')}
              className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Inventory Status
            </button>
            <div className="mt-2">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {item?.catalog_type || catalogType}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAdjustModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
            >
              <ArrowUpFromLine className="w-4 h-4" />
              Remove Stock
            </button>
          <button
              onClick={() => setShowReceiveModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors text-sm font-medium"
          >
              <ArrowDownToLine className="w-4 h-4" />
              Add Stock
          </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Current Stock</div>
            <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{totals.onHand}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Reserved</div>
            <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{totals.reserved}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Available</div>
            <div className="mt-2 text-3xl font-bold text-emerald-600 dark:text-emerald-500">{totals.available}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Avg Cost</div>
            <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{fmtMoney(avgCost)}</div>
            {item?.unit_price && (
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Sell: {fmtMoney(item.unit_price)}</div>
            )}
          </div>
        </div>

        {/* Unified Transaction Ledger */}
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Stock Movements</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Click any highlighted cell to edit inline
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Lot #</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Qty</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Balance</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Unit Cost</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Expires</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Notes</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {ledgerWithBalance.map((entry) => (
                  <tr 
                    key={entry.id} 
                    className={`transition-colors ${
                      entry.isEditable 
                        ? 'hover:bg-blue-50/50 dark:hover:bg-blue-900/10' 
                        : 'hover:bg-gray-50 dark:hover:bg-gray-900/30'
                    }`}
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                      {entry.source === 'transaction' ? fmtDateTime(entry.date) : fmtDate(entry.date)}
                  </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                      <div className="flex items-center gap-2">
                        <TypeIcon type={entry.type} />
                        {renderEditableCell(
                          entry,
                          'type',
                          <span>{entry.typeLabel}</span>,
                          entry.type
                        )}
                      </div>
                  </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {renderEditableCell(
                        entry,
                        'lotNumber',
                        <span className="font-mono">{entry.lotNumber || '—'}</span>,
                        entry.lotNumber,
                        'font-mono'
                      )}
                  </td>
                    <td className="px-4 py-3 text-sm text-right">
                      {renderEditableCell(
                        entry,
                        'quantity',
                        <span className={`font-semibold ${
                          entry.quantity > 0 ? 'text-emerald-600' : entry.quantity < 0 ? 'text-red-600' : 'text-gray-400'
                        }`}>
                          {entry.quantity > 0 ? `+${entry.quantity}` : entry.quantity}
                        </span>,
                        Math.abs(entry.quantity),
                        `font-semibold ${entry.quantity > 0 ? 'text-emerald-600' : entry.quantity < 0 ? 'text-red-600' : 'text-gray-400'}`
                      )}
                  </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white font-medium">
                      {entry.balance}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">
                      {/* Unit cost only makes sense for inbound (inflow type) entries */}
                      {entry.type === 'receipt' || entry.type === 'adjustment_in' ? (
                        renderEditableCell(
                          entry,
                          'unitCost',
                          <span>{fmtMoney(entry.unitCost)}</span>,
                          entry.unitCost ? entry.unitCost.replace('$', '') : ''
                        )
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {/* Expiration date is editable if the entry has a linked lot */}
                      {entry.lotId ? (
                        renderEditableCell(
                          entry,
                          'expirationDate',
                          <span>{entry.expirationDate ? fmtDate(entry.expirationDate) : '—'}</span>,
                          entry.expirationDate ? entry.expirationDate.split('T')[0] : ''
                        )
                      ) : (
                        <span>{entry.expirationDate ? fmtDate(entry.expirationDate) : '—'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-xs">
                      {renderEditableCell(
                        entry,
                        'notes',
                        <span className="truncate block" title={entry.notes || undefined}>
                          {entry.notes || '—'}
                        </span>,
                        entry.notes
                      )}
                    </td>
                    <td className="px-2 py-3">
                      {entry.isEditable && (
                        <button
                          onClick={() => handleDeleteRow(entry)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title="Delete row"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {ledgerWithBalance.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center">
                      <div className="text-gray-400 dark:text-gray-500">
                        <Package className="w-10 h-10 mx-auto mb-3 opacity-50" />
                        <p className="text-sm font-medium">No stock movements yet</p>
                        <p className="text-xs mt-1">Click "Add Stock" to add your first receipt</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modals */}
      {item && (
        <>
          <ReceiveStockModal
            isOpen={showReceiveModal}
            onClose={() => setShowReceiveModal(false)}
            item={item}
            vendors={vendors}
            onSave={handleReceiveStock}
          />
          <AdjustStockModal
            isOpen={showAdjustModal}
            onClose={() => setShowAdjustModal(false)}
            item={item}
            currentOnHand={totals.onHand}
            onSave={handleAdjustStock}
          />
        </>
      )}
    </div>
  );
};
